import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatAccordion } from '@angular/material/expansion';
import { Store } from '@ngrx/store';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { AuthService } from 'src/app/auth/auth.service';
import * as constants from 'src/app/config/app-constants';
import { Organization } from 'src/app/internal-user/customer-management/models/organization';
import { GenericConfirmationDialogComponent } from 'src/app/shared/generic-confirmation-dialog/generic-confirmation-dialog.component';
import { ToastService } from 'src/app/shared/services/toast.service';
import { FleetManagerActions } from 'src/app/store/actions';
import { State } from 'src/app/store/state';
import { DeploymentTypeMetadata, FeatureSelectedEventArgs, InputMode, InputType, UserInputPropertySource } from '../../models/deploymentForm.model';
import { FleetConfigurationService } from '../../services/fleet-configuration.service';
import { FleetManagementService } from '../../services/fleet-management.service';
import { SystemFormMetadataService } from '../../services/system-form-metadata.service';
import { SystemFormService } from '../../services/system-form.service';

export interface DeploymentFormDialogComponentData {
  edgeDeviceId: string;
  provisioned: boolean;
}

/**
 * Dialog containing the form for a full edge deployment; to deploy a pre-provisioned device or redeploy a provisioned device.
 * Utilizes metadata from the Fleet Management API to drive deployment options and related settings dynamically.
 */
@Component({
    selector: 'app-deployment-form-dialog',
    templateUrl: './deployment-form-dialog.component.html',
    styleUrls: ['./deployment-form-dialog.component.scss'],
    standalone: false
})
export class DeploymentFormDialogComponent implements OnInit {

  /** Static cache of deployment metadata used to create dynamic form components. */
  static systemFormMetadata: DeploymentTypeMetadata[];
  /** Data structure used to track all deployment-level properties available in the metadata with associated. */
  static deploymentLevelPropertyMap: Map<string, { propMetadata: UserInputPropertySource, featureReferences: Set<string>, required: boolean }>;

  /** Wrapper to expose InputMode to HTML template. */
  public get InputMode(): typeof InputMode { return InputMode; }

  @ViewChild(MatAccordion) accordion: MatAccordion;
  provisioned: boolean;
  selectedDeploymentType: string;
  accordionCollapsed: boolean = false;
  canEditAdvancedOptions: boolean = false;
  orderedKeys: string[] = [];
  selectedOrganization: Organization | null = null;
  /** Tracks the set of duplicate system IDs the user has already acknowledged for this submission attempt. */
  private acceptedDuplicateSystems: Set<string> = new Set<string>();

  /** The parent form for the entire deployment. */
  systemConfigForm: FormGroup;

  // Form helpers
  get organizationId(): string { return this.systemConfigForm.get('organizationId').value; }
  get deploymentFeatures(): FormArray { return this.systemConfigForm.get('deploymentFeatures') as FormArray; }
  get deploymentLevelProperties(): FormGroup { return this.systemConfigForm.get('deploymentLevelProperties') as FormGroup; }
  get systems(): FormArray { return this.systemConfigForm.get('systems') as FormArray; }

  // Expose static data to HTML
  get getFormMetadata(): DeploymentTypeMetadata[] { return DeploymentFormDialogComponent.systemFormMetadata }

  /** Deployment types filtered by LaunchDarkly feature flags. Only types without a flag or whose flag evaluates to true are shown. */
  visibleFormMetadata: DeploymentTypeMetadata[] = [];

  private refreshVisibleFormMetadata(): void {
    this.visibleFormMetadata = (DeploymentFormDialogComponent.systemFormMetadata || []).filter(m => this.systemFormService.isFeatureEnabled(m));
    this.visibleFormMetadata.sort((a, b) =>
      this.getSystemTypeDisplayName(a.friendlyName || a.name)
        .localeCompare(this.getSystemTypeDisplayName(b.friendlyName || b.name))
    );
  }

  get getDeploymentLevelPropertyMap(): Map<string, { propMetadata: UserInputPropertySource, featureReferences: Set<string>, required: boolean }> {
    return DeploymentFormDialogComponent.deploymentLevelPropertyMap;
  }

  /** Gets a list of each system type that is currently added to the form. */
  get distinctSystemTypes() {
    let names = this.systems.controls.map(control => this.getCanonicalSystemType(control.get('name').value));
    return [...new Set(names)];
  }

  getSystemTypeDisplayName(systemType: string): string {
    return this.getCanonicalSystemType(systemType);
  }

  getCanonicalSystemType(systemType: string): string {
    const normalizedSystemType = systemType?.replace(/[\s()_-]/g, '').toLowerCase();
    if (normalizedSystemType === 'atpcaps' || normalizedSystemType === 'tempoatp') {
      return 'Tempo-ATP';
    }

    return normalizedSystemType === 'caps' ? 'Tempo' : systemType;
  }

  /** Gets a list of all features enabled throughout the form. */
  get distinctEnabledFeatures(): Set<string> {
    let enabledFeatures = new Set<string>();

    function aggregateFeatureNames(features: any[]) {
      features.forEach(feature => {
        enabledFeatures.add(feature.name);

        if (feature.optionalFeatures) {
          aggregateFeatureNames(feature.optionalFeatures);
        }
      });
    }

    aggregateFeatureNames(this.deploymentFeatures.value || []);
    aggregateFeatureNames(this.systems.value);
    return enabledFeatures;
  }

  get availableDeploymentFeatures(): DeploymentTypeMetadata[] {
    const featuresByName = new Map<string, DeploymentTypeMetadata>();

    this.systems.controls.forEach(control => {
      const metadata = this.getSystemFormMetadata(control.get('name').value);
      (metadata?.deploymentFeatures || [])
        .filter(feature => this.systemFormService.isFeatureEnabled(feature))
        .forEach(feature => {
          if (!featuresByName.has(feature.name)) {
            featuresByName.set(feature.name, feature);
          }
        });
    });

    return Array.from(featuresByName.values())
      .sort((a, b) => (a.friendlyName || a.name).localeCompare(b.friendlyName || b.name));
  }

  get selectedDeploymentFeatureNames(): string[] {
    return (this.deploymentFeatures.value || []).map(feature => feature.name);
  }

  /** All deployment-level property keys whose input type is EdgeDeviceDropdown (for generic exclusion logic). */
  get edgeDeviceDropdownNames(): string[] {
    return this.orderedKeys.filter(k =>
      this.getDeploymentLevelPropertyMap.get(k)?.propMetadata?.userInputType === InputType.EdgeDeviceDropdown
    );
  }

  /** Returns true if any deployment-level property controls are currently displayed to the user. */
  get deploymentLevelPropertiesShown(): boolean {
    return Object.keys(this.deploymentLevelProperties.controls).some(key => this.showDeploymentLevelProperty(key));
  }

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public parentCompData: DeploymentFormDialogComponentData,
    public dialogRef: MatDialogRef<DeploymentFormDialogComponent>,
    public store: Store<State>,
    public systemFormService: SystemFormService,
    private fb: FormBuilder,
    private ngxLoader: NgxUiLoaderService,
    private toastService: ToastService,
    private fleetManagementService: FleetManagementService,
    private fleetConfigService: FleetConfigurationService,
    private authService: AuthService,
    private readonly systemFormMetadataService: SystemFormMetadataService,
    private readonly dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.provisioned = this.parentCompData.provisioned;
    let edgeDeviceId = this.parentCompData.edgeDeviceId;

    this.authService.profile$.subscribe({
      next: (profile) => {
        this.canEditAdvancedOptions = profile.hasRole("Parata Dev");
      },
      error: (err) => {
        console.error('Could not determine if current user has Parata Dev role, removing advanced options.');
        console.error(err);
        this.canEditAdvancedOptions = false;
      }
    });

    this.systemConfigForm = this.fb.group({
      edgeDeviceId: [edgeDeviceId, Validators.required],
      organizationId: ['', Validators.required],
      edgeDeviceFriendlyName: [''],
      // Needed so edge-device-select can pass ?currentDeploymentId= to available devices API (include bound peers)
      currentDeploymentId: [''],
      deploymentFeatures: this.fb.array([]),
      deploymentLevelProperties: this.fb.group({}),
      systems: this.fb.array([])
    });

    if (!DeploymentFormDialogComponent.systemFormMetadata) {
      const loadingKey = 'LoadFormMetadata';
      this.ngxLoader.start(loadingKey);

      this.fleetConfigService.getConfigTemplates().subscribe({
        next: metadata => {
          this.ngxLoader.stop(loadingKey);
          DeploymentFormDialogComponent.deploymentLevelPropertyMap = this.systemFormService.getDeploymentLevelPropertyMap(metadata);

          // Use local static cache for metadata, this will change very rarely
          DeploymentFormDialogComponent.systemFormMetadata = metadata;
          this.refreshVisibleFormMetadata();
          // Push into shared cache service so any dynamic children can subscribe.
          this.systemFormMetadataService.setMetadata(metadata);

          this.loadDevice(edgeDeviceId);
        },
        error: err => {
          console.error(err);
          this.ngxLoader.stop(loadingKey);
          this.toastService.openToast('Error fetching system form data, cannot configure the device.', constants.ToastPanelType.error);
          this.dialogRef.close(false);
        }
      });
    }
    else {
      // Metadata already cached statically, seed service immediately.
      this.systemFormMetadataService.setMetadata(DeploymentFormDialogComponent.systemFormMetadata);
      this.refreshVisibleFormMetadata();
      this.loadDevice(edgeDeviceId);
    }
  }

  /**
   * Load device details into form if applicable.
   * @param edgeDeviceId Edge device unique identifier
   */
  loadDevice(edgeDeviceId: string): void {
    if (!this.provisioned) {
      return;
    }

    const loadingKey = 'LoadProvisionedDevice';
    this.ngxLoader.start(loadingKey);

    this.fleetManagementService.getEdgeDeviceById(edgeDeviceId).subscribe({
      next: edgeDevice => {
        if (!edgeDevice) {
          console.error(`No provisioned device could be located with device ID ${edgeDeviceId}.`);
          this.toastService.openToast(`Provisioned device ${edgeDeviceId} could not be found.`, constants.ToastPanelType.error);
          this.dialogRef.close(false);
        }

        // Bind static controls
        this.systemConfigForm.patchValue({
          organizationId: edgeDevice.organizationId,
          edgeDeviceFriendlyName: edgeDevice.edgeDeviceFriendlyName,
          currentDeploymentId: edgeDevice.currentDeploymentId || ''
        });

        (edgeDevice.deploymentFeatures || []).forEach(feature => this.bindDeploymentFeature(feature));

        // Create and bind dynamic controls for deployment-level properties
        Object.keys(edgeDevice.deploymentLevelProperties || {}).forEach(propName => {
          if (this.getDeploymentLevelPropertyMap.has(propName)) {
            const propertyMapInfo = this.getDeploymentLevelPropertyMap.get(propName);
            this.toggleDeploymentLevelProperty(propertyMapInfo.propMetadata, true, propertyMapInfo.required);
          }
        });

        // Convert string booleans to actual booleans for deployment-level properties
        const deploymentLevelProperties = { ...edgeDevice.deploymentLevelProperties };
        Object.keys(deploymentLevelProperties).forEach(propName => {
          const propertyMapInfo = this.getDeploymentLevelPropertyMap.get(propName);
          if (
            propertyMapInfo &&
            propertyMapInfo.propMetadata.userInputType === InputType.CheckBox &&
            typeof deploymentLevelProperties[propName] === 'string'
          ) {
            deploymentLevelProperties[propName] = deploymentLevelProperties[propName].toLowerCase() === 'true';
          }
        });

        this.systemConfigForm.patchValue({
          deploymentLevelProperties: deploymentLevelProperties
        });

        edgeDevice.systems.sort((a, b) => {
          // Compare by name
          if (a.name < b.name) return -1;
          if (a.name > b.name) return 1;

          // If names are equal, compare by systemFriendlyName
          if (a.systemFriendlyName < b.systemFriendlyName) return -1;
          if (a.systemFriendlyName > b.systemFriendlyName) return 1;

          return 0; // If all comparisons are equal, return 0
        });

        // Create and bind dynamic controls for systems
        edgeDevice.systems.forEach(system => {
          if (!system) {
            console.warn('[DeploymentFormDialog] Encountered undefined system in edgeDevice.systems array.', edgeDevice.systems);
            return;
          }
          const metadata = this.getSystemFormMetadata(system.name);
          if (!metadata) {
            console.error(`[DeploymentFormDialog] No metadata found for system name '${system.name}'. Available metadata keys:`, DeploymentFormDialogComponent.systemFormMetadata?.map(m => m.name));
            return; // Skip to avoid undefined access inside bindSystemForm
          }
          console.debug('[DeploymentFormDialog] Binding system:', { systemName: system.name, systemId: system.systemId });
          let systemForm = this.systemFormService.bindSystemForm(metadata, system);
          systemForm.addControl('systemFriendlyName', new FormControl(system.systemFriendlyName));
          this.addSystem(systemForm, InputMode.View);
        });

        this.ngxLoader.stop(loadingKey);
      },
      error: err => {
        console.error(err);
        this.ngxLoader.stop(loadingKey);
        this.toastService.openToast(`Unexpected error fetching provisioned device ${edgeDeviceId}.`, constants.ToastPanelType.error);
        this.dialogRef.close(false);
      }
    });
  }

  /** Returns true if a given deployment-level property should be displayed to the user. */
  showDeploymentLevelProperty(key: string): boolean {
    const control = this.deploymentLevelProperties.get(key) as FormControl;

    if (!control) {
      console.warn('Key could not be found in deployment level property collection.')
      return false;
    }

    if (this.canEditAdvancedOptions || control.hasValidator(Validators.required)) {
      return true;
    }

    return false;
  }

  /**
   * Event handler invoked whenever optional features are added.
   * @param args Feature selected event data.
   */
  onFeatureSelect(args: FeatureSelectedEventArgs): void {
    (args.featureMetadata.deploymentLevelRequiredParameters || []).forEach(param => this.toggleDeploymentLevelProperty(param, args.selected, true));
    (args.featureMetadata.deploymentLevelOptionalParameters || []).forEach(param => this.toggleDeploymentLevelProperty(param, args.selected, false));
  }

  onDeploymentFeatureSelect(event: any, featureMetadata: DeploymentTypeMetadata): void {
    const selected = event.source.selected;

    if (event.isUserInput) {
      if (selected) {
        if (!this.isDeploymentFeatureSelected(featureMetadata.name)) {
          this.deploymentFeatures.push(this.getDeploymentFeatureFormGroup(featureMetadata));
        }
      }
      else {
        const featureIndex = this.deploymentFeatures.value.findIndex(feature => feature.name === featureMetadata.name);
        if (featureIndex >= 0) {
          this.deploymentFeatures.removeAt(featureIndex);
        }
      }
    }

    this.onFeatureSelect({
      featureMetadata: featureMetadata,
      selected: selected,
      isUserInput: event.isUserInput
    });
  }

  isDeploymentFeatureSelected(featureName: string): boolean {
    return this.selectedDeploymentFeatureNames.includes(featureName);
  }

  /**
   * Adds or removes a deployment-level property from the form.
   * @param source Metadata of the deployment level property.
   * @param selected Whether this property is being toggled on or off.
   * @param required Whether the property is required on the form.
   */
  toggleDeploymentLevelProperty(source: UserInputPropertySource, selected: boolean, required: boolean): void {
    if (selected) {
      if (this.deploymentLevelProperties.contains(source.name)) {
        return;
      }

      this.deploymentLevelProperties.addControl(source.name, this.systemFormService.createDynamicFormControl(source, required));
      this.orderedKeys.push(source.name); // Add key to orderedKeys
      // Sort by metadata sortOrder (ascending) when present
      this.orderedKeys.sort((a, b) => {
        const aMeta = this.getDeploymentLevelPropertyMap.get(a)?.propMetadata as any;
        const bMeta = this.getDeploymentLevelPropertyMap.get(b)?.propMetadata as any;
        const aOrder = (aMeta && typeof aMeta.sortOrder === 'number') ? aMeta.sortOrder : Number.MAX_SAFE_INTEGER;
        const bOrder = (bMeta && typeof bMeta.sortOrder === 'number') ? bMeta.sortOrder : Number.MAX_SAFE_INTEGER;
        if (aOrder === bOrder) return a.localeCompare(b);
        return aOrder - bOrder;
      });
    } else {
      const featureMap = this.getDeploymentLevelPropertyMap.get(source.name).featureReferences as any;

      if (!featureMap.isDisjointFrom(this.distinctEnabledFeatures)) {
        return;
      }

      if (this.deploymentLevelProperties.contains(source.name)) {
        this.deploymentLevelProperties.removeControl(source.name);
        this.orderedKeys = this.orderedKeys.filter(key => key !== source.name); // Remove key from orderedKeys
      }
    }
  }

  getFeatureList(systemType: string, features: Array<any>): string {
    return features.map(feature => {
      let systemFormMetadata = this.getSystemFormMetadata(systemType);
      let featureMetadata = systemFormMetadata.optionalFeatures.find(optionalFeature => optionalFeature.name === feature.name);
      return featureMetadata.friendlyName;
    }).sort((a, b) => a.localeCompare(b)).join(', ') || 'N/A';
  }

  getSystemFormMetadata(systemName: string): DeploymentTypeMetadata {
    const normalizedSystemName = systemName?.replace(/[\s()_-]/g, '').toLowerCase();
    let metadataSystemName = normalizedSystemName;

    if (normalizedSystemName === 'atpcaps') {
      metadataSystemName = 'tempoatp';
    }
    else if (normalizedSystemName === 'caps') {
      metadataSystemName = 'tempo';
    }

    return DeploymentFormDialogComponent.systemFormMetadata.find(data =>
      data.name?.replace(/[\s()_-]/g, '').toLowerCase() === metadataSystemName
    );
  }

  getSystemTypeCount(systemName: string): number {
    return this.systems.value.filter(system => this.getCanonicalSystemType(system.name) === systemName).length;
  }

  onAddSystemClick(systemName: string, event: Event): void {
    event?.stopPropagation();
    const form = this.getSystemFormMetadata(systemName);
    this.addSystem(this.systemFormService.getSystemForm(form), InputMode.Add);
  }

  addSystem(systemForm: FormGroup, mode: InputMode): void {
    systemForm.addControl('Mode', new FormControl(mode));

    this.onFeatureSelect({
      featureMetadata: this.getSystemFormMetadata(systemForm.get('name').value),
      selected: true
    });

    this.systems.push(systemForm);
  }

  editSystem(systemFormGroup: FormGroup): void {
    systemFormGroup.patchValue({
      Mode: InputMode.Edit
    });
  }

  validateAndSaveSystem(systemFormGroup: FormGroup): void {
    systemFormGroup.markAllAsTouched();

    if (systemFormGroup.valid) {
      systemFormGroup.patchValue({
        Mode: InputMode.View
      });
    }
  }

  deleteSystem(systemIndex: number): void {
    this.onFeatureSelect({
      featureMetadata: this.getSystemFormMetadata(this.systems.at(systemIndex).get('name').value),
      selected: false
    });

    this.systems.removeAt(systemIndex);
    this.removeUnavailableDeploymentFeatures();
  }

  private bindDeploymentFeature(feature: any): void {
    const featureMetadata = this.getDeploymentFeatureMetadata(feature.name);

    if (!featureMetadata || !this.systemFormService.isFeatureEnabled(featureMetadata) || this.isDeploymentFeatureSelected(feature.name)) {
      return;
    }

    this.deploymentFeatures.push(this.getDeploymentFeatureFormGroup(featureMetadata));
    this.onFeatureSelect({
      featureMetadata: featureMetadata,
      selected: true,
      isUserInput: false
    });
  }

  private getDeploymentFeatureFormGroup(feature: DeploymentTypeMetadata): FormGroup {
    return this.fb.group({
      name: [feature.name]
    });
  }

  private getDeploymentFeatureMetadata(featureName: string): DeploymentTypeMetadata {
    const featuresByName = new Map<string, DeploymentTypeMetadata>();

    (DeploymentFormDialogComponent.systemFormMetadata || []).forEach(metadata => {
      (metadata.deploymentFeatures || []).forEach(feature => {
        if (!featuresByName.has(feature.name)) {
          featuresByName.set(feature.name, feature);
        }
      });
    });

    return featuresByName.get(featureName);
  }

  private removeUnavailableDeploymentFeatures(): void {
    const availableFeatureNames = new Set(this.availableDeploymentFeatures.map(feature => feature.name));

    for (let index = this.deploymentFeatures.length - 1; index >= 0; index--) {
      const featureName = this.deploymentFeatures.at(index).get('name').value;

      if (availableFeatureNames.has(featureName)) {
        continue;
      }

      const featureMetadata = this.getDeploymentFeatureMetadata(featureName);
      this.deploymentFeatures.removeAt(index);

      if (featureMetadata) {
        this.onFeatureSelect({
          featureMetadata: featureMetadata,
          selected: false,
          isUserInput: false
        });
      }
    }
  }

  toggleAccordion(): void {
    if (this.accordionCollapsed) {
      this.accordionCollapsed = false;
      this.accordion.openAll();
    }
    else {
      this.accordionCollapsed = true;
      this.accordion.closeAll();
    }
  }

  showAlreadyProvisionedWarning(systems: string[]): void {
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      width: '480px',
      maxWidth: '90vw',
      disableClose: true
    });
    const instance = dialogRef.componentInstance;
    instance.dialogRef = dialogRef;
    instance.title = 'Already provisioned system';
    instance.message = `One or more selected systems (${systems.join(", ")}) are already provisioned to this Edge Appliance. Reusing an existing serial number that is already configured could lead to customer data corruption. Are you sure you want to continue?`;

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // Store the acknowledged duplicate systems and attempt submit again.
        this.acceptedDuplicateSystems = new Set(systems);
        this.submit();
      } else {
        // User declined; reset any previously accepted duplicates.
        this.acceptedDuplicateSystems.clear();
      }
    });
  }

  /**
   * Called by the organization-select component when a valid organization is chosen (or cleared).
   * Stores the full Organization object so that Incada-derived deployment controls can auto-populate.
   */
  onOrganizationSelected(org: Organization | null): void {
    this.selectedOrganization = org;
  }

  /** Submit the form to the Fleet Management API for deployment. */
  submit(): void {
    if (!this.systemConfigForm.valid) {
      this.toastService.openToast('Invalid deployment - double check the form for validation issues.', constants.ToastPanelType.error);
      this.systemConfigForm.markAllAsTouched();
      return;
    }

    let deploymentForm = this.systemConfigForm.getRawValue();

    let seenSystems = new Set<string>();
    let duplicateSystems = new Set<string>();
    deploymentForm.systems.forEach(system => {
      if (seenSystems.has(system.systemId)) {
        duplicateSystems.add(system.systemId);
      } else {
        seenSystems.add(system.systemId);
      }
    });
    
    if (duplicateSystems.size > 0) {
      const duplicatesAccepted = duplicateSystems.size === this.acceptedDuplicateSystems.size &&
        Array.from(duplicateSystems).every(id => this.acceptedDuplicateSystems.has(id));

      if (!duplicatesAccepted) {
        this.showAlreadyProvisionedWarning(Array.from(duplicateSystems));
        return; // Halt further submission until user acknowledges.
      }
    }

    // Remove Mode used for UI-specific tracking
    deploymentForm.systems.forEach(system => {
      const metadata = this.getSystemFormMetadata(system.name);
      this.systemFormService.setFeatureReferences(metadata, system);
      system.name = this.getCanonicalSystemType(system.name);
      delete system.Mode;
    });

    const loadingKey = 'SystemConfigurationSubmit';
    this.ngxLoader.start(loadingKey);

    let submit$ = this.provisioned
      ? this.fleetConfigService.putConfigTemplate(deploymentForm)
      : this.fleetConfigService.postConfigTemplate(deploymentForm);

    submit$.subscribe({
      next: () => {
        setTimeout(() => {
          this.ngxLoader.stop(loadingKey);
          this.toastService.openToast('Systems configured successfully.', constants.ToastPanelType.done);
          this.store.dispatch(FleetManagerActions.getIOTDeviceDetails());
          this.dialogRef.close(true);
          // Reset accepted duplicates after successful submission.
          this.acceptedDuplicateSystems.clear();
        }, 5000);
      },
      error: error => {
        this.ngxLoader.stop(loadingKey);
        console.error(error);
        // Reset accepted duplicates if submission failed.
        this.acceptedDuplicateSystems.clear();

        if (error.status === 500) {
          this.toastService.openToast('Unable to configure systems.', constants.ToastPanelType.error);
        }
        else {
          this.toastService.openToast(error.error, constants.ToastPanelType.error);
        }
      }
    });
  }
}
